import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { PageContentHeaderComponent } from '../page-content-header/page-content-header.component';
import { Call } from '@twilio/voice-sdk';
import { TranslationService } from '../../services/translation/translation.service';
import { TwilioMakeACallService } from '../../../care-manager-portal/components/twilio-make-a-call/service/twilio-make-a-call.service';
import { TwilioService } from '../../../patient-on-boarding-portal/service/twilio.service';
import { ToasterMessageService } from '../../services/topbar-toaster/toaster-message.service';
import {
  catchError,
  combineLatest,
  Observable,
  of,
  Subscription,
  tap,
  throwError,
} from 'rxjs';
import { NewCallContainerComponent } from '../new-call-container/new-call-container.component';
import { SelectComponent } from '../select/select.component';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CommonLookupService } from '../../services/lookup/common-lookup.service';
import { CommonHttpsService } from '../../services/https/common-https.service';
import { apiUrls } from '../../common/api_urls/api_urls';
import { HttpHeaders } from '@angular/common/http';
import { headers } from '../../common/headers';
import { GlobalService } from '../../services/common/global.service';
import { EncryptedService } from '../../services/encrypted-service/encrypted.service';
import { SwalalertService } from '../../services/swalalert/swalalert.service';
import { session } from '../../common/session/session';

// Calls whose patient profile was already auto-opened. Module-level because this modal is
// destroyed when minimised and recreated when reopened, and activeCall$ replays the live call.
const profileOpenedForCall = new WeakSet<Call>();

@Component({
  selector: 'app-incoming-call-modal',
  imports: [
    PageContentHeaderComponent,
    NewCallContainerComponent,
    SelectComponent,
    CommonModule,
    FormsModule,
  ],
  templateUrl: './incoming-call-modal.component.html',
  styleUrl: './incoming-call-modal.component.css',
})
export class IncomingCallModalComponent implements OnInit, OnDestroy {
  header = new HttpHeaders({
    [headers.twilioHeader]: 'true',
  });

  @Input()
  set newIncomingCall(call: any) {
    if (call) {
      this.addIncomingCall(call);
    }
  }
  private subscription: Subscription = new Subscription();

  private modalRef: NgbModalRef | null = null;

  // List of incoming calls
  incomingCalls: Array<{
    name?: string;
    number?: string;
    clinic?: string;
    avatar?: string;
    patientId?: string;
    sid?: string;
  }> = [];

  @Output() modalDismiss = new EventEmitter<void>();
  @Output() onEnd = new EventEmitter<any>();
  @Output() queueListUpdated = new EventEmitter<any[]>();
  @Output() onStartNextCall = new EventEmitter<any>();

  @Input() conferenceId: string = '';
  @Input() sid: string = '';
  @Input() fromNumber: string = '';
  @Input() programId: number | string | null = null;
  @Input() interactionTypeId: number | string | null = null;
  @Input() patientId: number | string | null = null;
  @Input() confSid: string = '';
  // Only show the Notes/Approve section once the after-call summary for
  // the current call has been generated and saved by the parent.
  @Input() summarySaved: boolean = false;

  // Current call data
  @Input() data: {
    name?: string;
    number?: string;
    clinic?: string;
    avatar?: string;
    patientId?: string;
    sid?: string;
  } = {
    name: 'Sarah Johnson',
    number: '+1 (555) 123-4567',
    clinic: 'Clinic Name here',
    avatar: '',
  };
  // Add incoming call when another call comes in
  addIncomingCall(call: any) {
    // Prevent adding the currently active call (by sid or number)
    console.log(this.data, call.number, this.data.number, call.number);
    this.incomingCalls.push(call);
    console.log('Added incoming call:', this.incomingCalls);
    if (this.data && call.number && this.data.number === call.number) {
      return;
    }
    // Prevent duplicates by sid

    // If a call is already active, log the new incoming call
    if (this.isOnCall) {
      console.log(
        'New incoming call received while another call is active:',
        call,
      );
    }
    this.closeIfNoCallsOrQueue();
  }

  // Example: Remove incoming call (when answered/rejected)
  removeIncomingCall(sid: string) {
    this.incomingCalls = this.incomingCalls.filter((c) => c.sid !== sid);
    this.closeIfNoCallsOrQueue();
  }

  isSwapped = false;

  private activeCall?: Call;
  private isStartingNextCall: boolean = false;
  private nextCallWaitSub: Subscription | null = null;
  private nextCallWaitTimeout: any = null;

  transferCallForm!: FormGroup;

  isOnCall = false;
  activeConnection: any = null;
  mic = true;

  @Input() callDuration: number = 0;

  isRegistered = false;

  queueList: any[] = [];

  List = [
    { id: 'manager', value: 'Manager' },
    { id: 'supervisor', value: 'Supervisor' },
    { id: 'director', value: 'Director' },
    { id: 'executive', value: 'Executive' },
    { id: 'analyst', value: 'Analyst' },
  ];

  groups: { id: any; value: string }[] = [];
  users: { id: any; value: string }[] = [];

  userId: number = 0;
  cognitoID: string | null = '';

  constructor(
    private fb: FormBuilder,
    public translationService: TranslationService,
    private twilioService: TwilioMakeACallService,
    private twilio: TwilioService,
    public messageService: ToasterMessageService,
    public commonService: CommonLookupService,
    public httpService: CommonHttpsService,
    private globalService: GlobalService,
    private swalalertService: SwalalertService,
    private router: Router,
    private encryptionService: EncryptedService,
  ) {
    this.initializeForm();
  }

  openPatientProfileInNewTab(): void {
    const id = this.data?.patientId;
    if (!id) {
      return;
    }
    localStorage.setItem(session.patientIdFollowUp, '');
    localStorage.setItem(session.patientIdFollowUp, String(id));
    const encryptedId = this.encryptionService.encrypt(id);
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/caremanager/profile-view/', encryptedId]),
    );
    window.open(url, '_blank');
  }

  ngOnInit(): void {
    this.userId = JSON.parse(this.globalService.getUserSettings()).userInfo.id;
    this.cognitoID = localStorage.getItem('cognitoID') || '';
    // Subscribe to shared active call state so we start/stop timer when call is accepted/ended
    this.subscription.add(
      (this.twilio as any).activeCall$?.subscribe((call: Call | undefined) => {
        this.activeCall = call;
        const nowOnCall = !!call;
        if (nowOnCall && !this.isOnCall) {
          this.isOnCall = true;
          this.fetchQueue();
          if (call && !profileOpenedForCall.has(call)) {
            profileOpenedForCall.add(call);
            this.openPatientProfileInNewTab();
          }
        } else if (!nowOnCall && this.isOnCall) {
          this.isOnCall = false;
          // if (this.incomingCalls.length === 0) {
          //   this.closeModal();
          // }
        }
      }) as any,
    );
    this.fetchQueue();

    const observables: Observable<any>[] = [
      this.getIVRGroups().pipe(catchError(() => of([]))),
    ];

    this.subscription.add(
      combineLatest(observables).subscribe(([groups]) => {
        this.groups = groups.map((g: any) => ({
          id: g.id,
          value: g.groupName,
        }));
      }),
    );

    this.transferCallForm.get('userType')?.valueChanges.subscribe((value) => {
      this.subscription.add(
        this.commonService.getUsersByGroup(value).subscribe((users) => {
          this.users = users;
        }),
      );
    });
  }

  getIVRGroups(): Observable<any> {
    return this.commonService.getIVRGroups();
  }

  initializeForm() {
    this.transferCallForm = this.fb.group({
      userType: ['', Validators.required],
      user: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.nextCallWaitSub) {
      this.nextCallWaitSub.unsubscribe();
      this.nextCallWaitSub = null;
    }
    if (this.nextCallWaitTimeout) {
      clearTimeout(this.nextCallWaitTimeout);
      this.nextCallWaitTimeout = null;
    }
  }

  formatCallDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    this.modalDismiss.emit(); // Notify the parent to close
  }

  clickHandlerBack() {
    this.closeModal();
  }

  endCall() {
    this.onEnd.emit();
    // Try to end any active Twilio call: prefer the shared TwilioService active call
    try {
      // Prefer the TwilioService-level disconnect which clears service state
      const twilioServiceDisconnect = (this.twilio as any).disconnectCall?.bind(
        this.twilio,
      );
      const sharedCall = (this.twilio as any).getActiveCall?.();

      if (twilioServiceDisconnect) {
        // This will call device.disconnectAll(), clear local state, and update observables
        twilioServiceDisconnect();
      } else if (sharedCall?.disconnect) {
        // Fallback: disconnect the active Call instance
        sharedCall.disconnect();
      } else if (this.activeConnection && this.activeConnection.disconnect) {
        this.activeConnection.disconnect();
      } else if (this.activeCall && this.activeCall.disconnect) {
        this.activeCall.disconnect();
      }
      console.log('Ended call', this.activeCall || sharedCall);
      // Ensure internal flags are reset
      try {
        (this.twilio as any).setActiveCall?.(undefined);
      } catch (e) {
        // ignore if not available
      }
    } catch (err) {
      console.warn('Failed to properly disconnect call', err);
    }

    this.messageService.infoMessage('Call Ended', true);

    // Close modal after ending the call
    this.closeModal();
  }

  toggleMic() {
    this.mic = !this.mic;
    if (this.activeCall) {
      this.activeCall.mute(!this.mic); // true to mute, false to unmute
      this.messageService.infoMessage(
        `Microphone is now ${!this.mic ? 'muted' : 'unmuted'}`,
        true,
      );
    } else {
      console.warn('No active call to toggle mic');
    }
  }

  translate(key: string): string {
    return this.translationService.translate(key);
  }

  transferCall() {
    const body = {
      conferenceSid: this.conferenceId,
      patientNumber: this.fromNumber,
      clientid: this.transferCallForm.get('user')?.value,
      twilionumber: this.fromNumber,
      patientId: this.data.patientId,
      CallSid: this.sid,
    };
    const header = new HttpHeaders({
      [headers.twilioHeader]: 'true',
    });

    this.subscription.add(
      this.httpService
        .AddEntity(apiUrls.twilioGenerateToken.transferCall, body, {
          headers: header,
        })
        .pipe(
          tap((data) => {}),
          catchError((error: any) => {
            // this.messageService.errroMessage(
            //   this.errorMessage?.ERRORMESSAGE || 'An error occurred',
            //   true,
            // );
            // this.loadingService?.stopSpinner?.();
            return throwError(() => error);
          }),
        )
        .subscribe(),
    );
  }

  leaveCall() {
    const body = {
      conferenceSid: this.conferenceId,
      callSid: this.sid,
    };

    this.subscription.add(
      this.httpService
        .AddEntity(apiUrls.twilioGenerateToken.removeParticipant, body, {
          headers: this.header,
        })
        .pipe(
          tap((data) => {
            this.endCall();
          }),
          catchError((error: any) => {
            // this.messageService.errroMessage(
            //   this.errorMessage?.ERRORMESSAGE || 'An error occurred',
            //   true,
            // );
            // this.loadingService?.stopSpinner?.();
            return throwError(() => error);
          }),
        )
        .subscribe(),
    );
  }

  getInitials(name: string): string {
    const parts = (name || '').trim().split(/\s+/);
    const first = parts[0] ? parts[0].charAt(0).toUpperCase() : '';
    const last =
      parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
    return (first + last).slice(0, 2);
  }

  notes: string = '';

  approveCall() {
    this.swalalertService
      .showConfirmAlert(
        'Are you sure?',
        'You want to resolve this patient?',
        'Yes',
        'No, cancel!',
      )
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }

        const body = {
          programId: this.programId,
          confSid: this.confSid,
          patientId: this.patientId,
          interactionTypeId: this.interactionTypeId,
          queueId: null,
          notes: this.notes,
          callType: 'live',
        };
        const header = new HttpHeaders({
          [headers.lookupHeader]: 'true',
        });

        this.subscription.add(
          this.httpService
            .UpdateEntity(apiUrls.caremanager.ivr.resolvedSummary, body, {
              headers: header,
            })
            .pipe(
              tap(() => {
                this.messageService.successMessage(
                  'Patient resolved successfully.',
                  true,
                );
                this.closeModal();
              }),
              catchError((error: any) => {
                return throwError(() => error);
              }),
            )
            .subscribe(),
        );
      });
  }

  addToQueue(call: any) {
    console.log('Adding call to queue:', call);
    const body = {
      callSid: call.sid,
      twilionumber: call.twilioNumber,
      createdBy: this.userId.toString(),
      conferenceSid: this.conferenceId,
      numberMappingId: 0,
      patientNumber: call.number,
      patientId: Number(call.patientId),
    };
    console.log(body);

    this.subscription.add(
      this.httpService
        .AddEntity(apiUrls.twilioGenerateToken.addToQueue, body, {
          headers: this.header,
        })
        .pipe(
          tap((data) => {
            this.fetchQueue();
            this.removeIncomingCall(call.sid);
          }),
          catchError((error: any) => {
            return throwError(() => error);
          }),
        )
        .subscribe(),
    );
  }

  fetchQueue() {
    this.subscription.add(
      this.httpService
        // .GetListwHead(apiUrls.twilioGenerateToken.queueList(this.cognitoID), {
        .GetListwHead(apiUrls.twilioGenerateToken.queueList(84), {
          headers: this.header,
        })
        .pipe(
          tap((data) => {
            console.log('Queue List:', data);
            this.queueList = data;
            this.queueListUpdated.emit(this.queueList);
            this.closeIfNoCallsOrQueue();
          }),
          catchError((error: any) => {
            return throwError(() => error);
          }),
        )
        .subscribe(),
    );
  }

  // Returns the formatted longest wait time (oldest createdOn in queueList)
  getLongestWaitTime(): string {
    if (!this.queueList || this.queueList.length === 0) return '00:00';
    const now = new Date();
    // Find the oldest createdOn
    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);
    const created = new Date(oldest.createdOn);
    let diff = Math.floor((now.getTime() - created.getTime()) / 1000); // seconds
    if (diff < 0) diff = 0;
    const h = Math.floor(diff / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((diff % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(diff % 60)
      .toString()
      .padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  // Returns the number of the oldest call in queueList
  getOldestQueueNumber(): string {
    if (!this.queueList || this.queueList.length === 0) return '';
    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);
    return oldest.patientNumber || '';
  }

  // Returns the name of the oldest call in queueList (if available)
  getOldestQueueName(): string {
    if (!this.queueList || this.queueList.length === 0) return '';
    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);
    return oldest.patientName || '';
  }

  pickUpNextCall() {
    if (!this.queueList || this.queueList.length === 0) return;
    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);
    console.log('Picking up next call from queue:', oldest);
    this.startNextCall(oldest);
  }

  startNextCall(data: any) {
    if (this.isStartingNextCall) {
      return;
    }
    this.isStartingNextCall = true;

    const runNextCall = () => {
      if (this.nextCallWaitSub) {
        this.nextCallWaitSub.unsubscribe();
        this.nextCallWaitSub = null;
      }
      if (this.nextCallWaitTimeout) {
        clearTimeout(this.nextCallWaitTimeout);
        this.nextCallWaitTimeout = null;
      }
      this.makeCall(data);
    };

    // End/disconnect the current running call
    try {
      const twilioServiceDisconnect = (this.twilio as any).disconnectCall?.bind(
        this.twilio,
      );
      const sharedCall = (this.twilio as any).getActiveCall?.();

      if (!sharedCall) {
        runNextCall();
        return;
      }

      if (twilioServiceDisconnect) {
        twilioServiceDisconnect();
      } else if (sharedCall?.disconnect) {
        sharedCall.disconnect();
      } else if (this.activeConnection && this.activeConnection.disconnect) {
        this.activeConnection.disconnect();
      } else if (this.activeCall && this.activeCall.disconnect) {
        this.activeCall.disconnect();
      }
      // Reset internal flags
      this.isOnCall = false;
      this.activeConnection = null;
      this.activeCall = undefined;
    } catch (err) {
      console.warn('Failed to properly disconnect call', err);
      this.isStartingNextCall = false;
      return;
    }

    this.nextCallWaitSub = (this.twilio as any).activeCall$?.subscribe(
      (call: Call | undefined) => {
        if (!call) {
          runNextCall();
        }
      },
    );

    this.nextCallWaitTimeout = setTimeout(() => {
      runNextCall();
    }, 4000);
  }

  async makeCall(callData: any) {
    try {
      const cognitoID = localStorage.getItem('cognitoID');
      const detail = `${callData.patientNumber}/0/${callData.patientId || 0}/${cognitoID}/incomingCall/incoming/${callData.callSid}/${callData.twilioNumber}`;
      console.log('Making call with detail:', detail);

      const call = await this.twilio.makeCall({ detail }, callData.patientId);

      if (call) {
        this.activeConnection = call;
        this.activeCall = call;
        // also register the active call in the TwilioService so it survives component recreation
        try {
          this.twilio.setActiveCall(call);
        } catch (e) {
          // ignore
        }
        this.isOnCall = true;
        this.fetchQueue();
        this.onStartNextCall.emit({
          name: callData.patientName || '',
          number: callData.patientNumber || '',
          clinic: callData.clinic || callData.ClinicName || '',
          patientId: callData.patientId || '',
        });

        this.messageService.infoMessage('Starting next call', true);

        call.on('disconnect', () => {
          this.isOnCall = false;
          this.activeConnection = null;
          this.activeCall = undefined;
          try {
            this.twilio.setActiveCall(undefined);
          } catch (e) {}
          // clear persisted call state and make component accept fresh patient data
          this.sid = '';
          this.messageService.infoMessage('Call Ended', true);
        });
      }
    } finally {
      this.isStartingNextCall = false;
    }
  }

  // Helper to close modal if no calls or queue and no active call
  private closeIfNoCallsOrQueue() {
    // const noQueue = this.queueList?.length === 0 || !this.queueList;
    // const noIncoming = this.incomingCalls?.length === 0 || !this.incomingCalls;
    // const noActiveCall = !this.isOnCall && !this.activeCall;
    // if (noQueue && noIncoming && noActiveCall) {
    //   this.closeModal();
    // }
  }
}
